// Client-presentable SEO summary — a designer can show this to a client as-is.
// Sourced from the persisted structured decision (never the markdown / GitHub).
// Default view is plain-language; technical builder notes live in an expander.
// A "Download PDF" action renders a branded, print-ready version (client-facing
// content only) via the browser's native print → Save as PDF (seoReportHtml).

import { seoReportHtml } from '../lib/seoReportHtml';
import { cleanSeoSummary } from '../lib/normalizeCopy';
import { btnGhost } from '../lib/ui.ts';

// Open a branded, self-printing SEO report in a new window (Save as PDF). Client-side only —
// the summary is already loaded, so there's no backend round-trip.
function downloadSeoPdf(summary: SeoClientSummary): void {
  const html = seoReportHtml(summary);
  const w = window.open('', '_blank');
  if (!w) return; // popup blocked — the button is a no-op rather than throwing
  w.document.open();
  w.document.write(html);
  w.document.close();
}

export interface SeoClientSummary {
  businessName: string;
  schemaType: string;
  areaServed: string[];
  positioningHeadline: string;
  llmsSummary: string;
  keywords: {
    term: string;
    volume: number | null;
    difficulty: number | null;
    intent: string | null;
    estimated?: boolean;
  }[];
  competitors: { name: string; url?: string; note?: string }[];
  pages: { path: string; title: string; meta: string }[];
  compliance: {
    collectsPersonalData: boolean;
    enquiriesGoTo: string;
    adsOrPixels?: string;
    visitorRegion: string;
    existingPrivacyPolicy?: string;
    posture: string;
  };
  notes?: string;
  dataMode: 'live' | 'estimated';
}

const SCHEMA_PLAIN: Record<string, string> = {
  LocalBusiness: 'a local business',
  Organization: 'an organisation',
  Corporation: 'a company',
  ProfessionalService: 'a professional service',
  Store: 'a shop',
  Restaurant: 'a restaurant',
};

function plainSchema(t: string): string {
  return SCHEMA_PLAIN[t] ?? `a ${t.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}`;
}

function pageName(path: string): string {
  if (path === '/' || path === '') return 'Home';
  const last = path.replace(/\/$/, '').split('/').filter(Boolean).pop() ?? path;
  return last.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function SeoSummary({ summary: raw }: { summary: SeoClientSummary }) {
  // Normalise the model-authored copy for client-facing display + the PDF export: em-dashes → a
  // plain hyphen, and any stray invisible/"watermark" Unicode scrubbed to ASCII. Idempotent.
  const summary = cleanSeoSummary(raw);
  // Defensive: the summary is persisted jsonb (meta.clientSummary) — an older/partial
  // record may be missing an array field; `?? []` keeps a render from crashing the
  // whole step (cluster #3 — the unguarded-array-join class of bug).
  const areaServed = summary.areaServed ?? [];
  const keywords = summary.keywords ?? [];
  const pages = summary.pages ?? [];
  const competitors = summary.competitors ?? [];
  const list = (xs: string[]) =>
    xs.length <= 1
      ? xs.join('')
      : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;

  return (
    <div className="space-y-5 card p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-ink-2">What we’ve set up</div>
          <h3 className="mt-0.5 text-lg font-semibold text-ink">
            {summary.businessName} - your search presence
          </h3>
          <p className="mt-1 text-sm text-ink-2">
            Here’s how we’ve set up {summary.businessName} to be found on Google and by AI
            assistants. You can share this page with the client as-is.
          </p>
        </div>
        <button
          type="button"
          onClick={() => downloadSeoPdf(summary)}
          className={`${btnGhost} shrink-0`}
        >
          Download PDF
        </button>
      </div>

      {/* Positioning */}
      <Section title="Your positioning">
        <p className="text-base font-medium text-ink">{summary.positioningHeadline}</p>
      </Section>

      {/* Where you show up */}
      <Section title="Where you’ll show up">
        <p className="text-sm text-ink-2">
          We’ve set you up as <b>{plainSchema(summary.schemaType)}</b>
          {areaServed.length > 0 && (
            <> serving <b>{list(areaServed)}</b></>
          )}
          .
        </p>
      </Section>

      {/* Keywords (plain — no metrics) */}
      <Section title="What your customers are searching for">
        <p className="mb-2 text-sm text-ink-2">
          The {keywords.length} searches we’re optimising your site for:
        </p>
        <div className="flex flex-wrap gap-1.5">
          {keywords.map((k) => (
            <span key={k.term} className="rounded-full bg-paper-2 px-2.5 py-0.5 text-xs text-ink-2">
              {k.term}
            </span>
          ))}
        </div>
      </Section>

      {/* Pages — the actual copy that will appear in search */}
      <Section title="Your pages in search results">
        <div className="space-y-3">
          {pages.map((p) => (
            <div key={p.path} className="rounded border border-line bg-paper-2 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-ink-2">
                {pageName(p.path)}
              </div>
              <div className="mt-0.5 text-sm font-semibold text-info">{p.title}</div>
              <div className="text-sm text-ink-2">{p.meta}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Competitors */}
      {competitors.length > 0 && (
        <Section title="Who else shows up for these searches">
          <ul className="space-y-1 text-sm text-ink-2">
            {competitors.map((c, i) => (
              <li key={i}>
                <span className="font-medium">{c.name}</span>
                {c.note && <span className="text-ink-2"> - {c.note}</span>}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* llms.txt summary */}
      <Section title="Your summary for AI assistants">
        <blockquote className="border-l-2 border-line pl-3 text-sm italic text-ink-2">
          {summary.llmsSummary}
        </blockquote>
      </Section>

      {/* Compliance */}
      <Section title="Privacy & compliance">
        <p className="text-sm text-ink-2">{summary.compliance.posture}</p>
        <p className="mt-1 text-xs text-ink-2">
          {summary.compliance.collectsPersonalData
            ? `You collect personal data; enquiries go to ${summary.compliance.enquiriesGoTo}.`
            : 'No personal data is collected.'}{' '}
          Primary visitor region: {summary.compliance.visitorRegion}.
        </p>
      </Section>

      {/* Builder detail — technical, not front-and-centre */}
      <details className="rounded border border-line bg-paper-2 p-3">
        <summary className="cursor-pointer text-sm font-medium text-ink-2">
          Builder detail (technical)
        </summary>
        <div className="mt-3 space-y-3">
          {summary.dataMode === 'estimated' && (
            <p className="rounded border border-warning-bright bg-warning-bright/10 p-2 text-xs text-warning">
              Figures are reasoned estimates — live search data (DataForSEO) was unavailable on
              this run. Re-run once configured to replace them.
            </p>
          )}
          {/* Phase 5 item 7: wrap the narrow table so it can't force a horizontal page scroll. */}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-ink-2">
                <tr>
                  <th className="py-1 pr-3">Keyword</th>
                  <th className="py-1 pr-3">Volume</th>
                  <th className="py-1 pr-3">Difficulty</th>
                  <th className="py-1 pr-3">Intent</th>
                </tr>
              </thead>
              <tbody>
                {keywords.map((k) => (
                  <tr key={k.term} className="border-t border-line">
                    <td className="py-1 pr-3">{k.term}{k.estimated ? ' *' : ''}</td>
                    <td className="py-1 pr-3">{k.volume ?? '—'}</td>
                    <td className="py-1 pr-3">{k.difficulty ?? '—'}</td>
                    <td className="py-1 pr-3">{k.intent ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {summary.notes && <p className="text-xs text-ink-2">{summary.notes}</p>}
          <ul className="list-disc space-y-0.5 pl-4 text-xs text-ink-2">
            <li>Map one primary keyword per page; support with related terms — don’t keyword-stuff.</li>
            <li>Write descriptive alt text for every image (accessibility + image search).</li>
            <li>Full builder handoff (schema inputs, all metrics) is the committed seo doc.</li>
          </ul>
        </div>
      </details>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h4 className="mb-1 text-sm font-semibold text-ink-2">{title}</h4>
      {children}
    </section>
  );
}
